// @ts-strict-ignore
import styled from 'styled-components';
import {
  Button, ButtonSize, ButtonVariant, DropdownMenu, TextField,
} from '@usealloy/component-library';
import TipCallout from '../TipCallout/TipCallout';
import { StyledRuleClause } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/RuleTemplate/styles';

export const StyledTipCallout = styled(TipCallout)`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  margin: 0;
  padding: 0;
`;

export const StyledAggregationCriteriaContainer = styled.div`
  flex: 6;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--cl-space-2);
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  align-self: flex-start;
`;

export const StyledAggregationSubEditorLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cl-space-2);
  width: 100%;
`;

export const StyledAggregationSubEditorRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: var(--cl-space-3);
  width: 100%;
`;

export const StyledAggregationSubEditorActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--cl-space-2);
`;

export const StyledAggregationSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--cl-space-2);
  width: 100%;
`;

export const StyledAggregationCriteriaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--cl-space-1);
  width: 100%;
`;

export const StyledFilterCriteriaRow = styled(StyledAggregationCriteriaRow)`
  flex-wrap: nowrap;
  padding: 0 var(--cl-space-1);
  border-radius: var(--cl-radius-1);
  transition: background-color 0.15s ease;

  > [data-filter-action] {
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover,
  &:focus-within {
    background-color: var(--cl-gray-200);

    > [data-filter-action] {
      opacity: 1;
    }
  }
`;

export const StyledDynamicValueLabel = styled.span`
  font-family: var(--cl-font-family-mono);
  font-size: var(--cl-font-size-2);
  font-weight: bold;
  line-height: var(--cl-line-height-3);
  color: var(--cl-text-default);
  padding: 0 var(--cl-space-1);
  white-space: nowrap;
`;

export const StyledAggregationNameDescriptionContainer = styled.div`
  flex: 4;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--cl-space-2);
  padding: var(--cl-space-3);
  background-color: var(--cl-gray-100);
  border-radius: var(--cl-radius-2);
  box-sizing: border-box;
`;

export const StyledAggregationSelectWrapper = styled.div`
  display: flex;
  align-items: center;
  height: fit-content;
  margin: 0.5px var(--cl-space-1) 0 0;
  flex-shrink: 0;
  flex-wrap: wrap;

  > div {
    display: flex;
    align-items: center;
  }
`;

/** Static clause text in aggregation criteria (e.g. "of", "Scope:", "to"). Built on StyledRuleClause; uses containerType for alignment. */
export const StyledAggregationClause = styled(StyledRuleClause).attrs({
  isEmptyPreviewOutcome: false,
})`
  color: var(--cl-text-secondary);
`;

/** Same as above but primary color */
export const StyledAggregationClausePrimary = styled(StyledAggregationClause)`
  color: var(--cl-text-primary);
`;

/** Trigger button for the Add/Remove Filter combobox. Extends component library Button (tertiary) with code-editor aesthetic overrides. */
export const StyledFilterTriggerButton = styled(Button).attrs({
  size: ButtonSize.small,
  variant: ButtonVariant.tertiary,
})`
  && {
    font-family: var(--cl-font-family-mono);
    font-size: var(--cl-font-size-2);
    font-weight: bold;
    line-height: var(--cl-line-height-3);
    background: transparent;
    border: none;
    padding: var(--cl-space-1) var(--cl-space-2);

    &:hover {
      background: var(--cl-yellow-200);
    }
  }
`;

/** Styled dropdown for the Add/Remove Filter menu. */
export const StyledFilterDropdownMenu = styled(DropdownMenu)`
  min-width: 292px;
  width: auto;
  border-radius: var(--cl-radius-3);
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.12);

  [data-advanced-checkbox='true'] {
    opacity: 1;
    pointer-events: auto;
  }
`;

/** Inline text input for filter static values. Extends component library TextField with code-editor aesthetic overrides. */
export const StyledFilterValueInput = styled(TextField).attrs({
  widthFitContent: true,
})`
  &&& {
    display: inline-flex;
    width: fit-content;
    margin: 0;
    border: none;
    border-radius: var(--cl-radius-1);
    background: transparent;
    font-family: var(--cl-font-family-mono);
    font-size: var(--cl-font-size-2);
    font-weight: bold;
    line-height: var(--cl-line-height-3);
    color: var(--cl-text-code);
    padding: 0 var(--cl-space-1);
    min-width: 80px;
    height: fit-content;
    caret-color: var(--cl-yellow-500);
    box-shadow: none;
    field-sizing: content;
  }

  &&&::placeholder {
    color: var(--cl-text-code);
    opacity: 0.6;
  }
  &&&:hover {
    background-color: var(--cl-yellow-200);
  }
  &&&:focus {
    background-color: var(--cl-yellow-200);
  }
`;

export const StyledPiiOperatorFooter = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--cl-space-1);
  padding: var(--cl-space-2);
  border-top: 1px solid var(--cl-gray-300);
  color: var(--cl-text-secondary);
  font-size: var(--cl-font-size-1);
  line-height: var(--cl-line-height-1);
  width: 0;
  min-width: 100%;
  box-sizing: border-box;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

export const StyledAggregationTextField = styled(TextField)<{ isPreview?: boolean }>`
  &&& {
    display: inline-block;
    width: fit-content;
    margin: 0;
    margin-top: 0;
    opacity: ${props => (props.isPreview ? '0.5' : '1')};
    border: none;
    border-radius: var(--cl-radius-1);
    font-family: var(--cl-font-family-mono);
    font-size: var(--cl-font-size-2);
    font-weight: bold;
    caret-color: var(--cl-yellow-500);
    background: transparent;
    color: var(--cl-text-default);
    height: fit-content;
    line-height: var(--cl-line-height-3);
    width: auto;
    max-width: 100%;
    field-sizing: content;

    &:hover {
      background-color: var(--cl-yellow-200);
    }

    &:focus {
      outline: none;
    }
  }

  &&& input {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    outline: none;
    font-weight: bold;
    font-size: var(--cl-font-size-2);
    line-height: var(--cl-line-height-3);
    font-family: var(--cl-font-family-mono);
    color: var(--cl-text-default);
    padding: 0 var(--cl-space-1);
    min-width: 1ch;
    max-width: none;
    field-sizing: content;
  }

  &&& input:focus {
    outline: none;
  }

  &&& input:focus-visible {
    outline: 2px solid var(--cl-border-focus-ring);
    outline-offset: 2px;
  }
`;

export const StyledFilterDatePickerWrapper = styled.div`
  display: inline-flex;
  width: fit-content;
  min-width: 0;

  > div {
    width: fit-content;
  }
`;
